'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Author, AuthorSocialLinks, PaginatedBlogPosts } from '../../types/blog';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import Breadcrumb from '../ui/Breadcrumb';
import { Pagination } from '../ui/Pagination';
import { ArticleIcon } from '../icons';
import { BlogCard } from '../Blog/BlogCard';
import blogStyles from '../Blog/Blog.module.css';
import styles from './AuthorDetail.module.css';

interface AuthorDetailProps {
  author: Author;
  posts: PaginatedBlogPosts;
}

const SOCIAL_LABELS: Record<keyof AuthorSocialLinks, string> = {
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  github: 'GitHub',
  website: 'Website',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

const SOCIAL_ORDER: Array<keyof AuthorSocialLinks> = [
  'twitter',
  'linkedin',
  'website',
  'github',
  'facebook',
  'instagram',
  'youtube',
];

export function AuthorDetail({ author, posts }: AuthorDetailProps) {
  const t = useTranslations('blog');
  const tCommon = useTranslations('common');
  const router = useRouter();
  const { localizeFullPath } = useLocalizedPath();

  const handlePageChange = (page: number) => {
    const base = localizeFullPath(`/author/${author.slug}`);
    const params = new URLSearchParams();
    params.set('page', page.toString());
    router.push(`${base}?${params.toString()}`);
  };

  const socialEntries = SOCIAL_ORDER.map((key) => ({
    key,
    url: author.socialLinks?.[key],
  })).filter((e): e is { key: keyof AuthorSocialLinks; url: string } =>
    Boolean(e.url),
  );

  const initials = author.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const breadcrumbItems = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    {
      label: tCommon('breadcrumb.blog'),
      path: '/blog',
    },
    { label: author.name, path: `/author/${author.slug}` },
  ];

  return (
    <div className={blogStyles.blogListingPage}>
      <div className={blogStyles.blogBreadcrumbBar}>
        <div className={blogStyles.blogContainer}>
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <div className={blogStyles.blogHero}>
        <div className={blogStyles.blogContainer}>
          <div className={styles.heroInner}>
            <div className={styles.avatarWrap}>
              {author.avatarUrl ? (
                <Image
                  src={author.avatarUrl}
                  alt={author.name}
                  fill
                  unoptimized
                  sizes="160px"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <div className={styles.avatarFallback}>{initials}</div>
              )}
            </div>
            <h1 className={blogStyles.blogHeroTitle}>{author.name}</h1>
            {author.jobTitle && (
              <p className={styles.jobTitle}>{author.jobTitle}</p>
            )}
            {author.bio && <p className={styles.bio}>{author.bio}</p>}
            {socialEntries.length > 0 && (
              <ul className={styles.socials}>
                {socialEntries.map(({ key, url }) => (
                  <li key={key}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer me"
                      className={styles.socialLink}
                      aria-label={SOCIAL_LABELS[key]}
                    >
                      {SOCIAL_LABELS[key]}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className={`${blogStyles.blogContainer} ${blogStyles.blogMainContent}`}>
        <h2 className={styles.postsHeading}>
          {t('author.moreFrom', { name: author.name })}
        </h2>

        {posts.items.length > 0 ? (
          <div style={{ marginBottom: 48 }}>
            {posts.items.slice(0, 2).length > 0 && (
              <div className={blogStyles.blogFeaturedGrid}>
                {posts.items.slice(0, 2).map((post) => (
                  <BlogCard key={post._id} post={post} variant="large" eagerImage />
                ))}
              </div>
            )}

            {posts.items.slice(2).length > 0 && (
              <div className={blogStyles.blogRegularGrid}>
                {posts.items.slice(2).map((post) => (
                  <div key={post._id} className={blogStyles.blogRegularItem}>
                    <BlogCard post={post} variant="small" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={blogStyles.blogEmptyState}>
            <div className={blogStyles.blogEmptyIcon}>📝</div>
            <h2 className={blogStyles.blogEmptyTitle}>{t('author.noPosts')}</h2>
          </div>
        )}

        {posts.totalPages > 1 && (
          <div className={blogStyles.blogPaginationWrap}>
            <Pagination
              currentPage={posts.page}
              totalPages={posts.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link href={localizeFullPath('/blog')} className={styles.backToBlog}>
            ← {tCommon('breadcrumb.blog')}
          </Link>
        </div>
      </div>
    </div>
  );
}
